import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, Users, Video, Briefcase } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Events = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(response.data);
    } catch (error) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("+10 points! Successfully registered for event");
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register");
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "webinar": return <Video className="w-6 h-6" />;
      case "career_fair": return <Briefcase className="w-6 h-6" />;
      case "workshop": return <Users className="w-6 h-6" />;
      default: return <CalendarIcon className="w-6 h-6" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case "webinar": return "from-blue-500 to-cyan-500";
      case "career_fair": return "from-purple-500 to-pink-500";
      case "workshop": return "from-green-500 to-emerald-500";
      default: return "from-orange-500 to-red-500";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="events-container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <CalendarIcon className="w-10 h-10 text-indigo-500" />
            {t('events.title')}
          </h1>
          <p className="text-gray-600 text-lg">{t('events.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => {
            const isRegistered = event.attendees?.includes(user?.id);
            const isFull = event.max_attendees && event.attendees?.length >= event.max_attendees;
            
            return (
              <Card key={event.id} className="card-hover" data-testid={`event-card-${event.id}`}>
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getEventColor(event.type)} flex items-center justify-center mb-4 text-white`}>
                    {getEventIcon(event.type)}
                  </div>
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="capitalize">{event.type.replace('_', ' ')}</Badge>
                    {isRegistered && <Badge variant="outline" className="bg-green-50 text-green-700">{t('events.registered')}</Badge>}
                  </div>
                  <CardTitle className="text-lg">{event.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{event.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{t('events.speaker')}: {event.speaker}</span>
                    </div>
                    {event.max_attendees && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{event.attendees?.length || 0}/{event.max_attendees} {t('events.registered')}</span>
                      </div>
                    )}
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => handleRegister(event.id)}
                    disabled={isRegistered || isFull}
                    data-testid={`register-event-${event.id}`}
                  >
                    {isRegistered ? t('events.alreadyRegistered') : isFull ? t('events.eventFull') : t('events.registerNow')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {events.length === 0 && (
          <div className="text-center py-12" data-testid="no-events-message">
            <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">{t('events.noResults')}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Events;